import { Close as CloseIcon } from '@mui/icons-material';
import { Alert, Box, IconButton, Snackbar, Typography } from '@mui/material';
import React, { FunctionComponent } from 'react';
import { connect } from 'react-redux';
import { toastActions, toastSelectors } from 'store/glue/toast';
import { TOAST_DURATION } from 'store/toastSchema';
import Types from 'Types';

const mapStateToProps = (state: Types.RootState) => ({
  toasts: toastSelectors.toasts(state),
});

const mapDispatchToProps = (dispatch: Types.Dispatch) => ({
  dismissToast: (id: string) => dispatch(toastActions.dismissToast(id)),
});

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

// Renders active toasts stacked at the bottom-right of the screen.
// The outer Box uses pointerEvents: none so toasts don't block page interactions;
// each Snackbar restores pointerEvents: auto to allow close button clicks.
const _Toast: FunctionComponent<Props> = ({ toasts, dismissToast }) => (
  <Box
    sx={{
      bottom: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      pointerEvents: 'none',
      position: 'fixed',
      right: 16,
      zIndex: (t) => t.zIndex.snackbar,
    }}
  >
    {toasts.map((toast) => (
      <Snackbar
        key={toast.id}
        open
        autoHideDuration={TOAST_DURATION[toast.severity]}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          dismissToast(toast.id);
        }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={{ pointerEvents: 'auto', position: 'static' }}
      >
        <Alert
          severity={toast.severity}
          variant="standard"
          action={
            <IconButton
              size="small"
              color="inherit"
              onClick={() => dismissToast(toast.id)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {toast.title ? (
            <Typography variant="text1">{toast.title}</Typography>
          ) : null}
          {toast.message ? (
            <>
              {'\n'}
              <Typography variant="text2">{toast.message}</Typography>
            </>
          ) : null}
        </Alert>
      </Snackbar>
    ))}
  </Box>
);

export default connect(mapStateToProps, mapDispatchToProps)(_Toast);
